/**
 * Cart drawer upsell row — "Complete the Adventure"
 *
 * - Right arrow advances the scroller by exactly one card (wraps to the start
 *   once the last card is reached).
 * - "Add +" posts the variant to /cart/add.js, then re-renders the cart drawer
 *   in place and restores the scroll position of the row.
 * - All listeners are delegated on document, so they survive the drawer being
 *   replaced after a cart update.
 */
(function () {
  'use strict';

  if (window.__cartUpsellRowInit) return;
  window.__cartUpsellRowInit = true;

  var SCROLL_KEY = 'cartUpsellRowScrollLeft';

  function getTrack(el) {
    var root = el.closest('[data-cart-upsell]');
    return root ? root.querySelector('[data-cart-upsell-track]') : null;
  }

  function cardStep(track) {
    var card = track.querySelector('.cta-upsell__card');
    if (!card) return Math.round(track.clientWidth / 3);
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function scrollToLeft(track, left) {
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      track.scrollLeft = left;
    }
  }

  function advance(track) {
    var max = track.scrollWidth - track.clientWidth;
    var next = track.scrollLeft + cardStep(track);
    if (track.scrollLeft >= max - 2) {
      scrollToLeft(track, 0); // wrap back to the first card
    } else {
      scrollToLeft(track, Math.min(next, max));
    }
  }

  function syncArrows(root) {
    var track = root.querySelector('[data-cart-upsell-track]');
    var arrow = root.querySelector('[data-cart-upsell-next]');
    if (!track || !arrow) return;
    var overflows = track.scrollWidth > track.clientWidth + 4;
    arrow.classList.toggle('is-hidden', !overflows);
  }

  function syncAll() {
    document.querySelectorAll('[data-cart-upsell]').forEach(syncArrows);
  }

  function saveScroll(track) {
    try {
      sessionStorage.setItem(SCROLL_KEY, String(track.scrollLeft));
    } catch (e) {
      /* storage blocked — not critical */
    }
  }

  function restoreScroll() {
    var saved;
    try {
      saved = sessionStorage.getItem(SCROLL_KEY);
    } catch (e) {
      saved = null;
    }
    if (saved === null) return;
    document.querySelectorAll('[data-cart-upsell-track]').forEach(function (track) {
      track.scrollLeft = parseFloat(saved) || 0;
    });
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = !!busy;
    button.classList.toggle('is-loading', !!busy);
  }

  function refreshCart() {
    return fetch(window.location.pathname + '?refresh-cart=1', {
      headers: { Accept: 'text/html' },
    })
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        var newDrawer = doc.querySelector('cart-drawer');
        var currentDrawer = document.querySelector('cart-drawer');
        if (newDrawer && currentDrawer) {
          currentDrawer.replaceWith(newDrawer);
          if (typeof newDrawer.connectedCallback === 'function') newDrawer.connectedCallback();
          if (typeof newDrawer.open === 'function') newDrawer.open();
        }

        var newCartForm = doc.querySelector('main cart-form, #MainContent cart-form, .container cart-form');
        var currentCartForm = document.querySelector('main cart-form, #MainContent cart-form, .container cart-form');
        if (newCartForm && currentCartForm && currentCartForm !== currentDrawer) {
          currentCartForm.replaceWith(newCartForm);
        }

        restoreScroll();
        syncAll();
      })
      .catch(function (err) {
        console.error('Cart upsell row: failed to refresh cart', err);
        window.location.reload();
      });
  }

  function addVariant(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (j) {
          throw new Error((j && j.description) || 'Could not add product');
        });
      }
      return res.json();
    });
  }

  /* Arrow */
  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('[data-cart-upsell-next]');
    if (!arrow) return;
    e.preventDefault();
    var track = getTrack(arrow);
    if (track) advance(track);
  });

  /* Variant picker keeps the button in sync */
  document.addEventListener('change', function (e) {
    var select = e.target.closest('[data-cart-upsell-select]');
    if (!select) return;
    var card = select.closest('.cta-upsell__card');
    var button = card ? card.querySelector('[data-cart-upsell-add]') : null;
    if (button) button.dataset.variantId = select.value;
  });

  /* Add to cart */
  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-cart-upsell-add]');
    if (!button) return;
    e.preventDefault();

    var variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    var track = getTrack(button);
    if (track) saveScroll(track);

    setBusy(button, true);
    addVariant(variantId)
      .then(refreshCart)
      .catch(function (err) {
        console.error('Cart upsell row: add to cart failed', err);
        setBusy(button, false);
      });
  });

  document.addEventListener('DOMContentLoaded', syncAll);
  window.addEventListener('resize', syncAll);

  /* The drawer can be re-rendered by the theme itself (qty changes, other
     upsells). Re-sync whenever a new row shows up in the DOM. */
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches('[data-cart-upsell]') || node.querySelector('[data-cart-upsell]')) {
            syncAll();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  syncAll();
})();
